/**
 * i18n — système de traduction maison FR/EN.
 *
 * Détection de langue (ordre de priorité) :
 *   1. localStorage ('census.lang')
 *   2. URL hash query (#/?lang=en ou #/species?lang=en)
 *   3. navigator.language (fr-* → 'fr', sinon 'en')
 *   4. 'fr' par défaut
 *
 * API publique :
 *   getLang()              → 'fr' | 'en'
 *   setLang(lang)          → change la langue, persiste, met à jour <html lang>, redéclenche le router
 *   t(key, params?)        → chaîne traduite, avec interpolation {param} et pluriel (|)
 */

import fr from './locales/fr.js';
import en from './locales/en.js';

const SUPPORTED = ['fr', 'en'];
const STORAGE_KEY = 'census.lang';
const LOCALES = { fr, en };

function getHashLang() {
  const hash = window.location.hash.slice(1);
  const qIdx = hash.indexOf('?');
  if (qIdx === -1) return null;
  return new URLSearchParams(hash.slice(qIdx + 1)).get('lang');
}

function detect() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  const hashLang = getHashLang();
  if (hashLang && SUPPORTED.includes(hashLang)) return hashLang;
  const nav = (navigator.language || '').slice(0, 2).toLowerCase();
  if (SUPPORTED.includes(nav)) return nav;
  return 'fr';
}

let currentLang = detect();

// Pose document.documentElement.lang au chargement du module
document.documentElement.lang = currentLang;

export function getLang() {
  return currentLang;
}

export function setLang(lang) {
  if (!SUPPORTED.includes(lang) || lang === currentLang) return;
  currentLang = lang;
  localStorage.setItem(STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  window.dispatchEvent(new CustomEvent('lang-change'));
}

/**
 * Traduit une clé, avec interpolation de paramètres et gestion du pluriel.
 *
 * Pluriel : 'singular | plural' — utilise params.count pour choisir.
 * Interpolation : '{param}' est remplacé par params[param].
 *
 * @example
 *   t('list.count', { count: 3 })  // '3 espèces'
 *   t('depth.upTo', { max: 40 })   // 'jusqu'à 40 m'
 */
export function t(key, params = {}) {
  const raw = LOCALES[currentLang]?.[key] ?? LOCALES.fr[key] ?? key;

  let str = raw;

  // Pluralisation : 'singular | plural', choix basé sur params.count
  if (typeof str === 'string' && str.includes('|') && params.count != null) {
    const parts = str.split('|').map((s) => s.trim());
    str = params.count === 1 ? parts[0] : parts[1];
  }

  // Interpolation des paramètres {clé}
  return str.replace(/{(\w+)}/g, (_, k) =>
    params[k] != null ? String(params[k]) : `{${k}}`
  );
}
